import { Checkbox, IntegrationLogo } from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import {
	inheritsProtection,
	ownsProtection,
	protectionToSettings,
	settingsToProtection
} from 'components/protection-settings/protection-adapters'
import { useSignalCoverage } from 'components/protection-settings/signal-coverage'
import { ProtectionSettings } from 'components/protection-settings/protection-values'
import { ProtectionSettingsCard } from 'components/protection-settings/protection-settings-card'
import { CampaignDTO } from 'components/breadcrumb-bar/campaign-status'
import { useEffect, useState } from 'react'
import { useAccountStore, useAssetStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { ClickDetectionBanner } from './click-detection-banner'
import { setCampaignDetection } from './ignored-campaigns'
import { adAccountLabel, platformToProvider } from './connection-helpers'
import { ConnectionsSheet, ConnectionSection } from './connections-sheet'

/**
 * Campaign protection settings (DEV-2422 §2.5). No tabs — protection only.
 *
 * The chain is website → ad account → campaign. `parentValues` is the ad account's
 * EFFECTIVE set, which is the website's again whenever the account inherits in turn;
 * only the table above knows that, so it is resolved there and handed down.
 */
export const CampaignDrawer = ({
	campaign,
	adAccount,
	parentValues,
	clickDetectionOn,
	open,
	onClose
}: {
	campaign: CampaignDTO
	adAccount: AdAccountDTO
	/** The ad account's effective settings — resolved by the caller, not here. */
	parentValues: ProtectionSettings
	/** Resolved across BOTH levels: an account that is off stops every campaign under it. */
	clickDetectionOn: boolean
	open: boolean
	onClose: () => void
}) => {
	const user = useUserStore((s) => s.user)
	const { demoMode, setCampaignProtection, refreshAdAccounts } = useAssetStore(
		useShallow((s) => ({
			demoMode: s.demoMode,
			setCampaignProtection: s.setCampaignProtection,
			refreshAdAccounts: s.refreshAdAccounts
		}))
	)
	const { account, userAccess } = useAccountStore(
		useShallow((s) => ({ account: s.account, userAccess: s.userAccess }))
	)

	const canWrite = !demoMode && userAccess(user?.id || '') !== 'READ'

	// Masks plan-gated signals out of the payload; the card already renders them off.
	const covers = useSignalCoverage()
	const [inherit, setInherit] = useState(inheritsProtection(campaign))
	const [saveSlot, setSaveSlot] = useState<HTMLElement | null>(null)

	useEffect(() => {
		if (open) setInherit(inheritsProtection(campaign))
	}, [open, campaign])

	const handleSave = async (next: ProtectionSettings | null) => {
		if (!account) return
		// `null` means "inherit from now on". `enabled: true` travels with it because
		// `findProtectionConfig` reads `enabled` BEFORE `inherit` — an inheriting campaign
		// left at `enabled: false` gets no protection rather than its parent's.
		const settings = next ?? parentValues
		await setCampaignProtection(
			account.id,
			adAccount.id,
			campaign.id,
			settingsToProtection(settings, campaign.protection, {
				inherit: next === null,
				covers
			}),
			adAccount.platform
		)

		// §2.5 — monitoring off with "Keep detecting clicks from this campaign" unticked
		// turns this campaign's click detection off, which IS adding it to the ad
		// account's ignored list (§2.3). One-way, and this campaign only: turning
		// monitoring back on does not take it out of the list again.
		//
		// `next` only, never the inherit branch — a save with the box ticked hands the
		// campaign back to its ad account rather than deciding anything about its clicks.
		if (next && !next.aiEnabled && !next.keepTracking) {
			await setCampaignDetection(
				adAccount,
				campaign.id,
				false,
				refreshAdAccounts
			)
		}
	}

	return (
		<ConnectionsSheet
			open={open}
			onClose={onClose}
			title="Campaign settings"
			plain
			footer={
				<div
					ref={setSaveSlot}
					className="flex items-center justify-end gap-3"
				/>
			}
		>
			<div className="flex flex-col gap-4">
				{!clickDetectionOn && <ClickDetectionBanner scope="campaign" />}

				<ConnectionSection className="flex items-center gap-3">
					<IntegrationLogo
						provider={platformToProvider(adAccount.platform)}
						size={24}
					/>
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-base font-medium text-t-default">
							{campaign.name} ({campaign.id})
						</span>
						<span className="truncate text-sm text-t-secondary">
							{adAccountLabel(adAccount)}
						</span>
					</div>
				</ConnectionSection>

				<div className="flex flex-col gap-1 border-b border-[rgb(var(--cg-border))] pb-4">
					<Checkbox
						size="sm"
						id="campaign-inherit-protection"
						checked={inherit}
						disabled={!canWrite}
						label="Inherit ad account protection settings"
						onChange={(e) => setInherit(e.target.checked)}
					/>
					<p className="pl-6 text-[12px] leading-4 text-t-secondary">
						This campaign follows the settings of{' '}
						<span className="font-medium text-t-default">
							{adAccountLabel(adAccount)}
						</span>
						. Untick the box to give this campaign its own settings.
					</p>
				</div>

				<ProtectionSettingsCard
					// Remount on the entity or the tick, because `initialValues` is read once
					// — which is also what discards an unsaved draft on re-tick.
					key={`${campaign.id}-${inherit}`}
					bare
					scope="campaign"
					inherited={inherit}
					savedInherited={inheritsProtection(campaign)}
					parentValues={parentValues}
					initialValues={
						ownsProtection(campaign)
							? protectionToSettings(campaign.protection)
							: parentValues
					}
					saveSlot={saveSlot}
					onSave={handleSave}
				/>
			</div>
		</ConnectionsSheet>
	)
}
